import React, {FC, memo, useState} from "react";
import {
    BankOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CreditCardOutlined,
    FileTextOutlined,
    GlobalOutlined,
    LinkOutlined,
    RollbackOutlined,
    WalletOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {Tag, Tooltip} from "antd";
// import i18n from "../../services/i18n";
import {PaymentMethodType} from "../../helpers/getPaymentMethodType";
import {PaymentProvider, PaymentTransferType} from "../../generated/graphql";
import {mollieCdnIconUrl, mollieMethodLabel} from "../../helpers/mollieMethodLabels";
import {MOLLIE_FALLBACK_ICONS} from "../../assets/payment-icons/mollie";
import {useCurrency} from "../../helpers/currencyHelper";
import {CurrencyShort, renderAmount} from "../../helpers/renderAmount";
import {badgeStateLabel, BadgeInput, BadgeState, deriveBadgeState, refundBaseAmount} from "./deriveBadgeState";

// BY PAYMENT METHOD TYPE --------------------------------------------------------------------------------------------------------------------
type PaymentMethodMap = {
    [key in PaymentMethodType]: string;
};

type PaymentIconMap = {
    [key in PaymentMethodType]: React.ReactNode;
};

const paymentMethodTypeIcons: PaymentIconMap = {
    sepaDebit: <BankOutlined style={{marginRight: 3}} />,
    esr: <FileTextOutlined style={{marginRight: 3}} />,
    payPal: <BankOutlined style={{marginRight: 3}} />,
    creditCard: <CreditCardOutlined style={{marginRight: 3}} />,
    cash: <WalletOutlined style={{marginRight: 3}} />,
    bankTransfer: <BankOutlined style={{marginRight: 3}} />,
    onlinePayment: <GlobalOutlined style={{marginRight: 3}} />,
    none: <BankOutlined style={{marginRight: 3}} />,
};

const pamyentMethodTypeTranslations: PaymentMethodMap = {
    sepaDebit: "Lastschrift",
    esr: "Einzahlungsschein",
    creditCard: "Kreditkarte",
    payPal: "PayPal",
    cash: "Bar",
    bankTransfer: "Überweisung",
    onlinePayment: "Online-Zahlung",
    none: "Keine",
};

export const paymentMethodTypeLabels: PaymentMethodMap = {
    sepaDebit: pamyentMethodTypeTranslations.sepaDebit,
    esr: pamyentMethodTypeTranslations.esr,
    creditCard: pamyentMethodTypeTranslations.creditCard,
    cash: pamyentMethodTypeTranslations.cash,
    bankTransfer: pamyentMethodTypeTranslations.bankTransfer,
    payPal: pamyentMethodTypeTranslations.payPal,
    onlinePayment: pamyentMethodTypeTranslations.onlinePayment,
    none: pamyentMethodTypeTranslations.none,
};

// BY PAYMENT TRANSFER TYPE --------------------------------------------------------------------------------------------------------------------

type PaymentTransferMap = {
    [key in PaymentTransferType]: string;
};

type PaymentTransferIconMap = {
    [key in PaymentTransferType]: React.ReactNode;
};

const paymentTransferTypeIcons: PaymentTransferIconMap = {
    SepaDebit: <BankOutlined style={{marginRight: 3}} />,
    Esr: <FileTextOutlined style={{marginRight: 3}} />,
    Paypal: <BankOutlined style={{marginRight: 3}} />,
    Creditcard: <CreditCardOutlined style={{marginRight: 3}} />,
    Cash: <WalletOutlined style={{marginRight: 3}} />,
    BankTransfer: <BankOutlined style={{marginRight: 3}} />,
    Online: <GlobalOutlined style={{marginRight: 3}} />,
    None: <BankOutlined style={{marginRight: 3}} />,
};

export const paymentTransferTypeLabels: PaymentTransferMap = {
    SepaDebit: pamyentMethodTypeTranslations.sepaDebit,
    Esr: pamyentMethodTypeTranslations.esr,
    Creditcard: pamyentMethodTypeTranslations.creditCard,
    Cash: pamyentMethodTypeTranslations.cash,
    BankTransfer: pamyentMethodTypeTranslations.bankTransfer,
    Paypal: pamyentMethodTypeTranslations.payPal,
    Online: pamyentMethodTypeTranslations.onlinePayment,
    None: pamyentMethodTypeTranslations.none,
};

// MOLLIE METHOD ICON -------------------------------------------------------------------------------------------------

/**
 * Kleine Icon-Komponente für Mollie-Methoden.
 *
 * Primary: offizielle CDN-URL von Mollie
 * (https://www.mollie.com/external/icons/payment-methods/<method>.svg).
 * Fallback: lokale kompakte SVG aus `assets/payment-icons/mollie/`. Der
 * Fallback greift genau einmal pro Render (State-Flag `failed`), um eine
 * Endlosschleife bei dauerhaft broken CDN-Links zu vermeiden.
 */
const MollieMethodIcon: FC<{method: string; size?: number}> = ({method, size = 16}) => {
    const [failed, setFailed] = useState(false);
    const fallbackSrc = MOLLIE_FALLBACK_ICONS[method];
    const src = failed && fallbackSrc ? fallbackSrc : mollieCdnIconUrl(method);

    const handleError = () => {
        if (!failed && fallbackSrc) {
            setFailed(true);
        }
    };

    return (
        <img
            src={src}
            alt=""
            aria-hidden="true"
            onError={handleError}
            style={{
                width: size,
                height: size,
                marginRight: 4,
                verticalAlign: "middle",
                objectFit: "contain",
            }}
        />
    );
};

// LIFECYCLE-BADGE (Phase 16 / BADGE-01, BADGE-02) --------------------------------------------------------------------

/**
 * Visual-Mapping für die 9 Lifecycle-States (D-23).
 * Farben folgen AntD-4 Tag-Palette. Icons aus `@ant-design/icons`.
 * Die Beschriftung kommt aus `badgeStateLabel` (i18n, zur Render-Zeit).
 */
type BadgeVisual = {
    color: string;
    icon?: React.ReactNode;
};

const BADGE_VISUAL: {[key in BadgeState]: BadgeVisual} = {
    [BadgeState.Paid]: {color: "green", icon: <CheckCircleOutlined />},
    [BadgeState.Open]: {color: "default"},
    [BadgeState.LinkSent]: {color: "blue", icon: <LinkOutlined />},
    [BadgeState.MethodChanged]: {color: "gold", icon: <WarningOutlined />},
    [BadgeState.Expired]: {color: "red", icon: <CloseCircleOutlined />},
    [BadgeState.Failed]: {color: "red", icon: <CloseCircleOutlined />},
    [BadgeState.Refunded]: {color: "orange", icon: <RollbackOutlined />},
    [BadgeState.PartiallyRefunded]: {color: "orange", icon: <RollbackOutlined />},
    [BadgeState.Chargebacked]: {color: "red", icon: <WarningOutlined />},
    // Grau statt rot: kein Fehler, sondern ein bewusst beendeter Zahlungsvorgang.
    // Icon unterscheidet sie vom icon-losen "Offen"-Badge derselben Farbe.
    [BadgeState.Canceled]: {color: "default", icon: <CloseCircleOutlined />},
    [BadgeState.LinkVoided]: {color: "default", icon: <CloseCircleOutlined />},
};

/**
 * DD.MM.-Formatter ohne Jahreszahl. Tooltip-kompakt (D-24).
 */
function formatDateDDMM(iso: string): string {
    return new Date(iso).toLocaleDateString("de-DE", {day: "2-digit", month: "2-digit"});
}

/**
 * Zusatz für den Refund-Tooltip, wenn bei Mollie weniger gezahlt wurde als die
 * Rate heute ausweist (Altdaten vor dem Edit-Lock, Referenzfall B-104). Ohne
 * diesen Hinweis widerspricht der Tooltip sichtbar der Betrags-Spalte:
 * "Voll erstattet: 1 €" neben einer Zeile, die 2 € anzeigt.
 */
function rateHint(sp: BadgeInput, amount: (value: number) => string): string {
    if (sp.molliePaidAmount == null) return "";
    if (Number(sp.molliePaidAmount) >= Number(sp.paymentAmount)) return "";
    return " (Rate: " + amount(sp.paymentAmount) + ")";
}

/**
 * Baut den dynamischen Tooltip-Text pro State (D-24).
 * Rückgabe: einzeiliger String. Fallback "Link aktiv" wenn linkExpiresAt null.
 *
 * `currency` kommt aus den Tenant-Einstellungen und wird über denselben
 * Formatter gejagt wie die Betrags-Spalten (`RenderAmount`) — sonst steht im
 * Tooltip "294.00 EUR", während die Zeile daneben "294 CHF" anzeigt.
 */
export function buildBadgeTooltip(state: BadgeState, sp: BadgeInput, currency: CurrencyShort = "EUR"): string {
    const amount = (value: number): string => renderAmount({amount: value, currency, short: true});

    switch (state) {
        case BadgeState.LinkSent: {
            const iso =
                sp.linkExpiresAt ||
                (sp.latestMolliePaymentAttempt && sp.latestMolliePaymentAttempt.linkExpiresAt) ||
                null;
            if (!iso) return "Link aktiv";
            return "Link gesendet — gültig bis " + formatDateDDMM(iso);
        }
        case BadgeState.MethodChanged: {
            if (!sp.methodChangedAt) return "Methode geändert";
            return "Methode geändert am " + formatDateDDMM(sp.methodChangedAt);
        }
        case BadgeState.PartiallyRefunded:
            // "von X erstattet" statt "X von Y": Ohne das Verb liest sich
            // "25 von 294" wie ein Restbetrag statt wie eine Erstattung.
            return amount(Number(sp.providerRefundedAmount || 0)) + " von " + amount(refundBaseAmount(sp)) + " erstattet" + rateHint(sp, amount);
        case BadgeState.Refunded:
            return "Voll erstattet: " + amount(refundBaseAmount(sp)) + rateHint(sp, amount);
        case BadgeState.Expired:
            return "Link abgelaufen";
        case BadgeState.Failed: {
            const reason =
                (sp.latestMolliePaymentAttempt && sp.latestMolliePaymentAttempt.failureReason) ||
                "unbekannt";
            return "Zahlung fehlgeschlagen: " + reason;
        }
        case BadgeState.Chargebacked:
            return "Rückbuchung — siehe Chargeback-Übersicht";
        case BadgeState.Canceled:
            return "Zahlung storniert — nicht mehr bezahlbar";
        case BadgeState.LinkVoided:
            return "Zahlungslink entwertet — nicht mehr bezahlbar";
        case BadgeState.Paid:
            return "Bezahlt";
        case BadgeState.Open:
        default:
            return "Offen";
    }
}

// ------------------------------------------------------------------------------------------------------------------------------------

type PaymentMethodBadgeProps = {
    type?: PaymentMethodType;
    transferType?: PaymentTransferType;
    /**
     * Wenn der zugehörige ScheduledPayment/Booking via Mollie abgewickelt wurde,
     * setzt der Aufrufer `paymentProvider="Mollie"`. Zusammen mit einer nicht-leeren
     * `mollieMethod` (z.B. "creditcard", "paypal") überschreibt der Badge dann die
     * reguläre `type`/`transferType`-Darstellung und zeigt "Mollie: <Label>".
     */
    paymentProvider?: PaymentProvider | "Manual" | "Mollie";
    mollieMethod?: string | null;
    /**
     * Phase 16 / BADGE-01: Wenn gesetzt, rendert der Badge den 9-State-Lifecycle-
     * Zustand (Paid / Open / LinkSent / MethodChanged / Expired / Failed /
     * Refunded / PartiallyRefunded / Chargebacked) mit farbigem AntD-Tag + Icon
     * + dynamischem Tooltip (D-22/D-23/D-24). Hat Vorrang vor der Mollie-Method-
     * Badge-Darstellung und dem Legacy-Pfad.
     */
    sp?: BadgeInput | null;
};

const paymentProviderLabels: {[key in PaymentProvider]: string} = {
    [PaymentProvider.Manual]: "Manuell",
    [PaymentProvider.Mollie]: "Mollie",
};

const paymentProviderColors: {[key in PaymentProvider]: string} = {
    [PaymentProvider.Manual]: "default",
    [PaymentProvider.Mollie]: "blue",
};

const shouldShowProviderMarker = (
    paymentProvider: PaymentMethodBadgeProps["paymentProvider"],
    type: PaymentMethodType | undefined,
    transferType: PaymentTransferType | undefined,
): paymentProvider is PaymentProvider => {
    if (paymentProvider !== PaymentProvider.Manual && paymentProvider !== PaymentProvider.Mollie) {
        return false;
    }

    return type === "bankTransfer"
        || type === "sepaDebit"
        || transferType === PaymentTransferType.BankTransfer
        || transferType === PaymentTransferType.SepaDebit;
};

const renderProviderMarker = (paymentProvider: PaymentProvider | undefined) => {
    if (!paymentProvider) {
        return null;
    }

    return (
        <Tag color={paymentProviderColors[paymentProvider]} style={{marginLeft: 4, marginRight: 0}}>
            {paymentProviderLabels[paymentProvider]}
        </Tag>
    );
};

const PaymentMethodBadge: FC<PaymentMethodBadgeProps> = ({
    type,
    transferType,
    paymentProvider,
    mollieMethod,
    sp,
}) => {
    // Vor dem ersten conditional return — Hook-Reihenfolge muss stabil bleiben.
    const currency = useCurrency();

    // Phase 16 Lifecycle-Badge hat höchsten Vorrang. Wenn sp-Prop gesetzt
    // ist, rendert der Badge den 9-State-Zustand — unabhängig davon, ob der
    // Aufrufer zusätzlich type/transferType/mollieMethod mitgibt.
    if (sp) {
        const state = deriveBadgeState(sp);
        const visual = BADGE_VISUAL[state];
        const tooltip = buildBadgeTooltip(state, sp, currency.short as CurrencyShort);

        return (
            <Tooltip title={tooltip}>
                <Tag color={visual.color} icon={visual.icon}>
                    {badgeStateLabel(state)}
                </Tag>
            </Tooltip>
        );
    }

    // Mollie-Darstellung hat Vorrang, wenn provider+method gesetzt sind.
    // Bei fehlender `mollieMethod` fällt die Komponente auf den regulären
    // Badge (type/transferType) zurück, damit der Admin nie einen leeren
    // "Mollie: "-Tag sieht.
    // Kein "Mollie: "-Prefix — der separate Mollie-Flag-Tag an der Buchung
    // kennzeichnet die Buchung bereits als Mollie-Zahlung; ein zusätzliches
    // Label im Methode-Badge waere redundant und verbraucht zu viel Platz
    // in Tabellen-Spalten.
    if (paymentProvider === "Mollie" && mollieMethod) {
        const label = mollieMethodLabel(mollieMethod);

        return (
            <Tag style={{borderColor: "lightgrey"}}>
                <MollieMethodIcon method={mollieMethod} />
                {label}
            </Tag>
        );
    }

    if (!type && !transferType) {
        return null;
    }

    if (type) {
        const label = paymentMethodTypeLabels[type];

        const icon = paymentMethodTypeIcons[type];
        const providerMarker = shouldShowProviderMarker(paymentProvider, type, undefined)
            ? renderProviderMarker(paymentProvider)
            : null;

        return (
            <span>
                <Tag style={{borderColor: "lightgrey", marginRight: providerMarker ? 0 : undefined}}>
                    {icon} {label}
                </Tag>
                {providerMarker}
            </span>
        );
    }

    if (transferType) {
        const label = paymentTransferTypeLabels[transferType];

        const icon = paymentTransferTypeIcons[transferType];
        const providerMarker = shouldShowProviderMarker(paymentProvider, undefined, transferType)
            ? renderProviderMarker(paymentProvider)
            : null;

        return (
            <span>
                <Tag style={{borderColor: "lightgrey", marginRight: providerMarker ? 0 : undefined}}>
                    {icon} {label}
                </Tag>
                {providerMarker}
            </span>
        );
    }

    return null;
};

export default memo(PaymentMethodBadge);
